import { ApolloError, isApolloError } from 'apollo-client/errors/ApolloError';

export type ErrorWithMessage = {
	message: string
}

// https://kentcdodds.com/blog/get-a-catch-block-error-message-with-typescript
export class ErrorService {
	
	static defaultErrorMessage = "Something went wrong";
	static defaultErrorStatus  = 500;
	
	/* ---------------- GENERIC ERRORS ---------------- */
	
	static isErrorWithMessage( error: unknown ): error is ErrorWithMessage{
		
		return (
			( typeof error === 'object' )
			&& ( error !== null )
			&& ( 'message' in error )
			&& ( typeof ( error as Record<string, unknown> ).message === 'string' )
		)
	}
	
	static toErrorWithMessage( maybeError: unknown ): ErrorWithMessage{
		
		if( ErrorService.isErrorWithMessage( maybeError ) ) return maybeError
		
		try{
			
			return new Error( JSON.stringify( maybeError ) );
			
		} catch {
			
			// fallback in case there's an error stringifying the maybeError
			// like with circular references for example.
			return new Error( String( maybeError ) )
		}
	}
	
	static getErrorMessage( error: unknown ){
		
		return ErrorService.toErrorWithMessage( error ).message || ErrorService.defaultErrorMessage
	}
	
	/* ---------------- APOLLO / NETWORK ERRORS ---------------- */
	
	static maybeGetGraphQLNetworkErrors( error: any ){
		
		return error?.networkError?.result?.errors ?? undefined;
	}
	
	static isErrorWithGraphNetworkErrorMessages( error: unknown ){
		
		const maybeNetworkErrors = ErrorService.maybeGetGraphQLNetworkErrors( error );
		
		return (
			( typeof maybeNetworkErrors === 'object' )
			&& ( maybeNetworkErrors !== null )
			&& ( maybeNetworkErrors.length > 0 )
			&& ( 'message' in maybeNetworkErrors[ 0 ] )
			&& ( typeof ( maybeNetworkErrors[ 0 ] as Record<string, unknown> ).message === 'string' )
		)
	}
	
	static toNetworkErrorWithMessage( maybeError: unknown ): ErrorWithMessage{
		
		const maybeNetworkErrors = ErrorService.maybeGetGraphQLNetworkErrors( maybeError );
		
		if( !maybeNetworkErrors ) return new Error( ErrorService.defaultErrorMessage );
		
		try{
			
			return new Error( maybeNetworkErrors.map( e => e.message ).join( ',' ) );
			
		} catch {
			
			// fallback in case there's an error stringifying the maybeError
			// like with circular references for example.
			return new Error( String( maybeNetworkErrors ) )
		}
	}
	
	static getErrorWithinGraphNetworkErrorMessages( error: unknown ){
		
		if( ErrorService.isErrorWithGraphNetworkErrorMessages( error ) ){
			
			return ErrorService.toNetworkErrorWithMessage( error ).message || ErrorService.defaultErrorMessage
		}
		
		return ErrorService.toErrorWithMessage( error ).message || ErrorService.defaultErrorMessage
	}
	
	static getGQLErrorMessage( error: Error ){
		
		if( isApolloError( error ) ){
			
			return ErrorService.getErrorWithinGraphNetworkErrorMessages( error )
			
		} else {
			
			return ErrorService.getErrorMessage( error )
		}
	}
}

